import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Shield, Star, Users, Zap, PhoneCall, Wifi, Smartphone, TrendingDown, Flame } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { resolveSearchRoute } from '../utils/search';
import FloatingWords from './TypewriterText';
import { trackPartnerClick, trackWhatsAppClick } from '../utils/analytics';

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(resolveSearchRoute(searchValue));
    }
  };

  const trustIndicators = [
    { icon: <Users className="w-5 h-5" />, value: '500+', label: t('hero.trust.customers') },
    { icon: <Star className="w-5 h-5" />, value: '4.8/5', label: t('hero.trust.rating') },
    { icon: <Shield className="w-5 h-5" />, value: 'DSGVO', label: t('hero.trust.dsgvo') },
    { icon: <TrendingDown className="w-5 h-5" />, value: '50%', label: t('hero.trust.sparen') },
  ];

  const quickSearchOptions = [
    { label: t('nav.smartphones'), to: '/produkte/smartphones', icon: <Smartphone className="w-4 h-4" /> },
    { label: t('nav.handytarife'), to: '/produkte/handytarif', icon: <PhoneCall className="w-4 h-4" /> },
    { label: t('nav.internet'), to: '/internet-zuhause', icon: <Wifi className="w-4 h-4" /> },
    { label: t('nav.stromGas'), to: '/strom-gas', icon: <Zap className="w-4 h-4" /> },
  ];

  return (
    <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 text-white pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-sm mb-6"
          >
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success-500"></span>
            </span>
            <span className="text-primary-100">{t('hero.badge')}</span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-5 leading-tight text-balance flex items-center justify-center flex-wrap gap-x-4 md:gap-x-8"
          >
            <FloatingWords
              words={[
                { text: t('hero.tw3'), className: 'text-gradient bg-gradient-to-r from-accent-300 to-accent-500 bg-clip-text text-transparent' },
                { text: t('hero.tw1'), className: 'text-gradient bg-gradient-to-r from-secondary-300 to-secondary-500 bg-clip-text text-transparent' },
                { text: t('hero.tw2'), className: 'text-gradient bg-gradient-to-r from-success-300 to-success-500 bg-clip-text text-transparent' },
              ]}
            />
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-primary-100 mb-8 max-w-2xl mx-auto"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Search Bar */}
          <motion.form
            onSubmit={handleSearch}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-xl mx-auto mb-4"
          >
            <div className="flex gap-2 bg-white rounded-2xl p-2 shadow-premium">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="w-full py-2.5 text-neutral-700 placeholder:text-neutral-400 outline-none text-sm"
                />
              </div>
              <button type="submit" className="btn-primary text-sm py-2.5 px-5">
                {t('hero.searchBtn')}
              </button>
            </div>
          </motion.form>

          {/* Quick search tags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-2 mb-10"
          >
            {quickSearchOptions.map((opt) => (
              <Link
                key={opt.to}
                to={opt.to}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-sm text-primary-100 transition-colors"
              >
                {opt.icon}
                {opt.label}
              </Link>
            ))}
          </motion.div>

          {/* SUPER DEALS Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mb-8"
          >
            <div className="relative inline-flex items-center gap-2 bg-black rounded-full px-5 py-2 mb-4 overflow-hidden">
              <Flame className="w-5 h-5 text-orange-400 animate-pulse" />
              <span className="font-bold tracking-wider text-lg bg-gradient-to-b from-yellow-200 via-yellow-400 to-yellow-600 bg-clip-text text-transparent" style={{ textShadow: '0 0 12px rgba(255,180,0,0.8), 0 0 24px rgba(255,140,0,0.5)' }}>
                SUPER DEALS
              </span>
              <Flame className="w-5 h-5 text-orange-400 animate-pulse" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://www.communicationads.net/tc.php?t=18676C16024305T"
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackPartnerClick('O2 Angebote', 'hero_super_deals')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#006B99] hover:bg-[#0078A8] text-white font-bold rounded-xl shadow-lg transition-all hover:scale-105 text-base"
              >
                <Smartphone className="w-5 h-5" />
                O₂ Angebote
              </a>
              <a
                href="https://www.communicationads.net/tc.php?t=18676C31934310T"
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackPartnerClick('Ay Yildiz Angebote', 'hero_super_deals')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#E60000] hover:bg-[#D50000] text-white font-bold rounded-xl shadow-lg transition-all hover:scale-105 text-base"
              >
                <PhoneCall className="w-5 h-5" />
                Ay Yildiz Angebote
              </a>
              <a
                href="https://www.communicationads.net/tc.php?t=18676C22028808T"
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackPartnerClick('Handyvertrag Angebote', 'hero_super_deals')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#00A86B] hover:bg-[#009A62] text-white font-bold rounded-xl shadow-lg transition-all hover:scale-105 text-base"
              >
                <Wifi className="w-5 h-5" />
                Handyvertrag Angebote
              </a>
            </div>
          </motion.div>

          {/* Affiliate Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.48 }}
            className="flex flex-col sm:flex-row gap-3 justify-center mb-8"
          >
            <a
              href="https://ndirect.ppro.de/click/huGw"
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackPartnerClick('Handy Schnappchen', 'hero_affiliate')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-yellow-400 hover:bg-yellow-500 text-neutral-900 font-bold rounded-xl shadow-lg transition-all hover:scale-105 text-base"
            >
              <Smartphone className="w-5 h-5" />
              Handy Schnäppchen
            </a>
            <a
              href="https://ndirect.ppro.de/click/r129"
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackPartnerClick('Freenet Angebote', 'hero_affiliate')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-green-500 hover:bg-green-600 text-black font-bold rounded-xl shadow-lg transition-all hover:scale-105 text-base"
            >
              <TrendingDown className="w-5 h-5" />
              Freenet Angebote
            </a>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-3 justify-center mb-12"
          >
            <Link to="/angebote/tarif" className="btn-accent text-base px-8 py-3.5">
              {t('hero.ctaTarif')} <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href="https://wa.me/491797473491"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('hero_cta')}
              className="inline-flex items-center justify-center px-8 py-3.5 bg-white/10 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors text-base"
            >
              {t('hero.ctaWhatsapp')}
            </a>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto"
          >
            {trustIndicators.map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className="text-accent-400 mb-1">{item.icon}</div>
                <p className="text-2xl font-bold text-white">{item.value}</p>
                <p className="text-xs text-primary-200">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 80" className="w-full" fill="#ffffff" preserveAspectRatio="none">
          <path d="M0,40 C320,80 480,0 720,20 C960,40 1120,60 1440,30 L1440,80 L0,80 Z" />
        </svg>
      </div>
    </div>
  );
};

export default Hero;
